import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { baserowAPI, User } from '../services/baserowAPI';

export interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  loginWithGoogle: (email: string, name?: string) => Promise<boolean>;
  logout: () => void;
  isAuthenticated: boolean;
}

const STORAGE_KEY = 'auth_user';
const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true); // Commencer en mode loading
  const [error, setError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);

  // Restaurer l'utilisateur depuis le localStorage au chargement
  useEffect(() => {
    const storedUser = localStorage.getItem(STORAGE_KEY);
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        // Restaurer aussi le JWT token
        baserowAPI.restoreJWTFromStorage();
      } catch (e) {
        console.error('Erreur lors de la restauration de l\'utilisateur:', e);
        localStorage.removeItem(STORAGE_KEY);
      }
    }
    setInitialized(true);
    setLoading(false); // Arrêter le loading après l'initialisation
  }, []);

  const login = useCallback(
    async (email: string, name?: string): Promise<boolean> => {
      // Ne pas changer le loading si on est encore en cours d'initialisation
      if (initialized) {
        setLoading(true);
      }
      setError(null);

      try {
        // Vérifier si l'email est autorisé dans Baserow
        const userData = await baserowAPI.authenticateUserByEmail(email, name);

        if (!userData) {
          setError('Cet email n\'est pas autorisé à accéder à l\'application');
          if (initialized) {
            setLoading(false);
          }
          return false;
        }

        // Stocker l'utilisateur dans le localStorage
        localStorage.setItem(STORAGE_KEY, JSON.stringify(userData));
        setUser(userData);
        setError(null);
        if (initialized) {
          setLoading(false);
        }
        return true;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Erreur lors de la connexion';
        setError(errorMessage);
        if (initialized) {
          setLoading(false);
        }
        return false;
      }
    },
    [initialized]
  );

  const logout = useCallback(() => {
    setUser(null);
    setError(null);
    localStorage.removeItem(STORAGE_KEY);
    baserowAPI.logout();
  }, []);

  const value: AuthContextType = {
    user,
    loading,
    error,
    loginWithGoogle: login,
    logout,
    isAuthenticated: !!user && initialized, // Ajouter la condition initialized
  };

  // Ne pas bloquer le rendu, mais indiquer que l'auth n'est pas encore prête
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth doit être utilisé à l\'intérieur d\'un AuthProvider');
  }
  return context;
};
